/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import {getLogsBoxFilter} from './_requests'
import {LogBox, LogsBoxQueryResponse} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import { useAuth } from '../../../../auth'
import { useAlarmChangedInvalidator } from '../../../../../pages/sensors-v2/transports/WebSocketTransport'

const QueryResponseContext = createResponseContext<LogBox>(initialQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state, searchTerm} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])
  const {currentUser} = useAuth()

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  // Refresh the logs list whenever any box's alarm state flips, so newly
  // recorded firmware events appear without a manual refresh. Invalidates the
  // whole LOGS_BOX_LIST namespace (prefix match) since the cache key bakes in
  // current filters/query string.
  useAlarmChangedInvalidator(`${QUERIES.LOGS_BOX_LIST}-${query}`)

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.LOGS_BOX_LIST}-${query}`,
    async () => {
      const otherFilters = (state.filter || {}) as Object
      let searchParams = new URLSearchParams(query)
      const take = parseInt(searchParams.get('page') || '1') - 1
      const skip = searchParams.get('items_per_page')
      if (!state.sort) {
        const res = await getLogsBoxFilter({company_id: currentUser?.last_company_id, take, skip, id: searchTerm, ...otherFilters});
        return res;
      } else {
        const response: LogsBoxQueryResponse = await getLogsBoxFilter(
          {
            company_id: currentUser?.last_company_id, 
            take, skip,
            id: searchTerm,
            ...otherFilters,
          }
        )
        const logsBox = response?.data as LogBox[]

        let logsBoxSorted: LogBox[]
        if (logsBox.length > 1) {
          const key = state.sort as keyof LogBox
          logsBoxSorted = logsBox!.sort((a, b) => {
            if (state.order === 'asc')
              if (typeof a[key] !== 'boolean')
                return (a[key]?.toString() as string).localeCompare(b[key]?.toString() as string)
              else return (b[key]?.toString() as string).localeCompare(a[key]?.toString() as string)
            if (typeof a[key] !== 'boolean')
              return (b[key]?.toString() as string).localeCompare(a[key]?.toString() as string)
            else return (a[key]?.toString() as string).localeCompare(b[key]?.toString() as string)
          })
        } else {
          logsBoxSorted = logsBox
        }

        return logsBoxSorted as LogsBoxQueryResponse
      }
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return []
  }

  return (response?.data as LogBox[]) || []
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
